import { ReactNode } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Trans } from "@lingui/react/macro";

const barGrow = keyframes`
    0%, 100% { transform: scaleY(0.25); opacity: .55; }
    50% { transform: scaleY(1); opacity: 1; }
`;

const glowPulse = keyframes`
    0%, 100% { opacity: .10; transform: scale(0.9); }
    50% { opacity: .22; transform: scale(1.05); }
`;

const messageIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Card = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    padding: '48px 40px',
    maxWidth: 380,
    textAlign: 'center',
});

const Glow = styled('div')({
    position: 'absolute',
    top: 4,
    left: -18,
    width: 140,
    height: 140,
    borderRadius: '50%',
    background: 'radial-gradient(circle, var(--color-primary) 0%, transparent 70%)',
    filter: 'blur(6px)',
    animation: `${glowPulse} 2.6s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        opacity: 0.14,
    },
});

const BarsWrap = styled('div')({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    height: 56,
});

const Bar = styled('span')<{ delay: number; height: number }>(({ delay, height }) => ({
    display: 'block',
    width: 11,
    height,
    borderRadius: 6,
    background: 'var(--color-primary)',
    transformOrigin: 'bottom',
    animation: `${barGrow} 1.1s ease-in-out ${delay}s infinite`,
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        transform: 'scaleY(0.7)',
    },
}));

const BAR_HEIGHTS = [26, 40, 56, 34, 46];

/** Any route under this prefix should use `RedirectLoader` instead of a bare spinner
 * for its pre-mount loading states (auth check, lazy-chunk fetch), so the visual is
 * continuous once the actual redirect page takes over. */
export function isRedirectPath(pathname?: string | null) {
    return !!pathname?.startsWith('/redirect/');
}

interface RedirectLoaderProps {
    heading?: ReactNode;
    subtext?: ReactNode;
    /** Change this when `subtext` changes to re-trigger its fade-in transition. */
    subtextKey?: string | number;
}

function RedirectLoader({ heading, subtext, subtextKey }: RedirectLoaderProps) {
    return (
        <Card>
            <BarsWrap>
                <Glow />
                {BAR_HEIGHTS.map((height, i) => (
                    <Bar key={i} height={height} delay={i * 0.12} />
                ))}
            </BarsWrap>
            <div>
                <p className="theme-text-primary font-bold mb-1" style={{ fontSize: '1.05rem' }}>
                    {heading ?? <Trans>Just a moment…</Trans>}
                </p>
                {subtext && (
                    <p
                        key={subtextKey}
                        className="theme-text-secondary mb-0"
                        style={{ animation: `${messageIn} .3s ease-out` }}
                    >
                        {subtext}
                    </p>
                )}
            </div>
        </Card>
    );
}

export default RedirectLoader;
